import AddIcon from '@mui/icons-material/Add';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, Stack, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { deDE } from '@mui/x-data-grid/locales';
import { get, set } from 'lodash';
import React, { FC, useEffect, useMemo, useState } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import MuiSnackbar from 'src/components/ui-component/mui-snackbar/MuiSnackbar';
import { useAuthentication } from 'src/context/AuthContext';
import { useFetchPerson_Nutzerverwaltung } from 'src/hooks/FetchPerson';
import GetRecords from 'src/lib/api/GetRecords';
import PutRecord from 'src/lib/api/PutRecord';
import { APIS } from 'src/lib/constants/apis';
import { BENUTZERDATEN_PORTAL_ROLLE, NOT_FOUND_ERROR_MESSAGE } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { getFieldNamefromDataPath, isObjectEmpty, isTrue } from 'src/utils/helper';
import AddPerson from './nutzerverwaltung-details/content/add-person/AddPerson';
import EditPerson from './nutzerverwaltung-details/content/edit-person/EditPerson';

// import schemas
import OrganisationSchema from 'src/lib/paths/organisation_FULL_Paths.json';
import PersonSchema from 'src/lib/paths/person_FULL_Paths.json';
import UserSchema from 'src/lib/paths/user_Paths.json';
import { InhaltTrenner, InhaltWrapper } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
const P = PersonSchema.attributes;
const U_P = UserSchema.attributes;
const ORGA_P = OrganisationSchema.attributes;
const BASIS_BENUTZERNAME_PATH = P.Organisation.schema.attributes.Mailbenachrichtung_Praefix_Fuer_Benutzernamen.path;
// const BASIS_BENUTZERNAME_FIELD_NAME = getFieldNamefromDataPath(ORGA_P.Mailbenachrichtung_Praefix_Fuer_Benutzernamen.path);
// const ORGANISATION_FIELD_NAME = getFieldNamefromDataPath(P.Organisation.path);

const NutzerVerwaltung: FC<{}> = () => {
	const { authenticatedData } = useAuthentication();

	const [activeShowCase, setActiveShowCase] = useState<'list' | 'addPerson' | 'editPerson'>('list');
	const [personIdToEdit, setPersonIdToEdit] = useState<number>(0);
	const [createPersonSucess, setCreatePersonSuccess] = useState<boolean | null>(null);
	const [createBasisBenutzernameSuccess, setCreateBasisBenutzernameSuccess] = useState<boolean | null>(null);
	const [basisBenutzernameErrorMessage, setBasisBenutzernameErrorMessage] = useState<string>('');
	const [showCreateBasisBenutzernameDialog, setShowCreateBasisBenutzernameDialog] = useState<boolean>(false);
	const [isBasisBenutzernameLocallyLocked, setIsBasisBenutzernameLocallyLocked] = useState<boolean>(false);
	const [benutzernameFormValues, setBenutzernameFormValues] = useState<Record<string, any>>(() => {
		const initialValues: Record<string, any> = {};
		set(initialValues, BASIS_BENUTZERNAME_PATH, '');
		return initialValues;
	});
	const { loading: wsActive, data, error } = useFetchPerson_Nutzerverwaltung(get(authenticatedData, 'organisationId', ''), activeShowCase);

	const authenticatedUser = data?.find((user) => user.id === authenticatedData?.personId);
	const isauthenticatedAdminUser = hasAdminRole(get(authenticatedUser, P.Portal_Benutzerdaten_Portal_Rolle.path));
	const organisation = get(data, `0`, {});
	const organisationPrefix = get(organisation, P.Organisation.schema.attributes.Mailbenachrichtung_Praefix_Fuer_Benutzernamen.path, '');
	const basisBenutzername = String(get(benutzernameFormValues, BASIS_BENUTZERNAME_PATH, ''));
	const basisBenutzernamePreviewPrefix = basisBenutzername.trim() || 'BASIS-BENUTZERNAME';
	const isBasisBenutzernameLocked = Boolean(organisationPrefix) || isBasisBenutzernameLocallyLocked;
	const isCreateBasisBenutzernameDisabled = isBasisBenutzernameLocked || basisBenutzername.trim() === '';

	useEffect(() => {
		setBenutzernameFormValues((prev) => {
			const next = { ...prev };
			set(next, BASIS_BENUTZERNAME_PATH, organisationPrefix);
			return next;
		});
		if (organisationPrefix) {
			setIsBasisBenutzernameLocallyLocked(true);
		}
	}, [organisationPrefix]);

	const handleCreateBasisBenutzername = async () => {
		const basisBenutzernameTrimmed = basisBenutzername.trim();
		if (!basisBenutzernameTrimmed || isBasisBenutzernameLocked) return;

		try {
			const organisationId = get(authenticatedData, 'organisationId', -1);
			const prefixFieldName = getFieldNamefromDataPath(ORGA_P.Mailbenachrichtung_Praefix_Fuer_Benutzernamen.path);
			const organisationsWithSamePrefix = await GetRecords(
				APIS.Organisation,
				[],
				{ [prefixFieldName]: { $eq: basisBenutzernameTrimmed } },
				[],
				{ page: 1, pageSize: 5 },
				['id'],
			);
			const conflictingOrganisation = get(organisationsWithSamePrefix, 'data', []).find(
				(eachOrganisation: any) => get(eachOrganisation, 'id', -1) !== organisationId,
			);

			if (conflictingOrganisation) {
				setBasisBenutzernameErrorMessage('Der Basis-Benutzername ist bereits vergeben. Bitte wählen Sie einen anderen.');
				setCreateBasisBenutzernameSuccess(false);
				return;
			}

			await PutRecord(APIS.Organisation, get(authenticatedData, 'organisationId', -1), {
				data: {
					[getFieldNamefromDataPath(ORGA_P.Mailbenachrichtung_Praefix_Fuer_Benutzernamen.path)]: basisBenutzernameTrimmed,
				},
			});
			setBenutzernameFormValues((prev) => {
				const next = { ...prev };
				set(next, BASIS_BENUTZERNAME_PATH, basisBenutzernameTrimmed);
				return next;
			});
			setIsBasisBenutzernameLocallyLocked(true);
			setBasisBenutzernameErrorMessage('');
			setCreateBasisBenutzernameSuccess(true);
		} catch (_error) {
			setBasisBenutzernameErrorMessage('Der Basis-Benutzername konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.');
			setCreateBasisBenutzernameSuccess(false);
		}
	};

	const handleOpenCreateBasisBenutzernameDialog = () => {
		if (isCreateBasisBenutzernameDisabled) return;
		setShowCreateBasisBenutzernameDialog(true);
	};

	const handleCloseCreateBasisBenutzernameDialog = () => {
		setShowCreateBasisBenutzernameDialog(false);
	};

	const handleConfirmCreateBasisBenutzername = async () => {
		await handleCreateBasisBenutzername();
		handleCloseCreateBasisBenutzernameDialog();
	};

	const handleBasisBenutzernameChange = (fieldName: string, value: any) => {
		if (fieldName !== BASIS_BENUTZERNAME_PATH) return;
		setBenutzernameFormValues((prev) => {
			const next = { ...prev };
			set(next, fieldName, value);
			return next;
		});
	};

	const columns = useMemo(() => {
		let column = [
			{
				field: 'Name',
				headerName: 'Name',
				flex: 1,
			},
			{
				field: getFieldNamefromDataPath(P.Kontaktdaten_E_Mail.path),
				headerName: 'E-Mail',
				flex: 1,
			},
			{
				field: 'isAdmin',
				headerName: 'Administrative Rechte',
				width: 160,
				renderCell: (params: any) => {
					return isTrue(params.value) ?
							<>
								<Box
									sx={{
										display: 'inline-block',
										borderRadius: '100%',
										backgroundColor: '#4CAF50',
										width: '0.5rem',
										height: '0.5rem',
										marginRight: '1.125rem',
									}}
								></Box>
								Ja
							</>
						:	<>
								<Box
									sx={{
										display: 'inline-block',
										borderRadius: '100%',
										backgroundColor: '#F44336',
										width: '0.5rem',
										height: '0.5rem',
										marginRight: '1.125rem',
									}}
								></Box>
								Nein
							</>;
				},
			},
			{
				field: 'isUser',
				headerName: 'Benutzer*in',
				width: 120,
				renderCell: (params: any) => {
					return isTrue(params.value) ?
							<>
								<Box
									sx={{
										display: 'inline-block',
										borderRadius: '100%',
										backgroundColor: '#4CAF50',
										width: '0.5rem',
										height: '0.5rem',
										marginRight: '1.125rem',
									}}
								></Box>
								Ja
							</>
						:	<>
								<Box
									sx={{
										display: 'inline-block',
										borderRadius: '100%',
										backgroundColor: '#F44336',
										width: '0.5rem',
										height: '0.5rem',
										marginRight: '1.125rem',
									}}
								></Box>
								Nein
							</>;
				},
			},
			{
				field: 'status',
				headerName: 'Status',
				width: 90,
				renderCell: (params: any) => {
					return isTrue(params.value) ?
							<Chip label={'Aktiv'} color='success' variant={'outlined'} />
						:	<Chip label={'Inaktiv'} color='error' variant={'outlined'} />;
				},
			},
		];
		return column;
	}, []);

	const rows = useMemo(() => {
		let row = data?.map((eachPerson: any) => {
			return {
				...get(eachPerson, 'attributes', {}),
				id: get(eachPerson, 'id'),
				Name: get(eachPerson, P.Personenbezogene_Daten_Vorname.path, '') + ' ' + get(eachPerson, P.Personenbezogene_Daten_Nachname.path, ''),
				isAdmin: hasAdminRole(get(eachPerson, P.Portal_Benutzerdaten_Portal_Rolle.path, '')),
				isUser: !isObjectEmpty(get(eachPerson, P.User.path, {})),
				status: get(eachPerson, `${P.User.path}.attributes.${U_P.confirmed.path}`),
			};
		});
		return row;
	}, [data]);

	if (error) {
		return <MUIAlert infoType={'error'} title='Error' message={NOT_FOUND_ERROR_MESSAGE} />;
	}

	if (activeShowCase === 'addPerson') {
		return (
			<AddPerson
				handleToggleActiveShowCase={() => setActiveShowCase('list')}
				setCreatePersonSuccess={(value: boolean | null) => setCreatePersonSuccess(value)}
			/>
		);
	}
	if (activeShowCase === 'editPerson' && personIdToEdit) {
		return <EditPerson personId={personIdToEdit} handleToggleActiveShowCase={() => setActiveShowCase('list')} />;
	}
	return (
		<>
			<MuiSnackbar
				showSnackbar={Boolean(basisBenutzernameErrorMessage)}
				closeSnackbar={() => setBasisBenutzernameErrorMessage('')}
				alertMessage={basisBenutzernameErrorMessage}
				error={true}
			/>
			<MuiSnackbar
				showSnackbar={isTrue(createBasisBenutzernameSuccess)}
				closeSnackbar={() => setCreateBasisBenutzernameSuccess(null)}
				alertMessage='Basis-Benutzername wurde erfolgreich gespeichert'
			/>
			<MuiSnackbar
				showSnackbar={isTrue(createPersonSucess)}
				closeSnackbar={() => setCreatePersonSuccess(null)}
				alertMessage='Person wurde erfolgreicht angelegt'
			/>
			<Dialog
				open={showCreateBasisBenutzernameDialog}
				onClose={handleCloseCreateBasisBenutzernameDialog}
				maxWidth='xs'
				fullWidth
				PaperProps={{ sx: { backgroundColor: '#fff', borderRadius: '0.25rem' } }}
			>
				<DialogContent sx={{ pt: '1.75rem', pb: '0.75rem' }}>
					<Stack alignItems='center' spacing={1.5}>
						<Box>
							<WarningAmberRoundedIcon sx={{ color: '#f28c28' }} />
						</Box>
						<Typography variant='h4' fontWeight={600} textAlign='center'>
							Basis-Benutzername festlegen
						</Typography>
						<Typography variant='body2' textAlign='center' sx={{ color: '#667085' }}>
							Achtung: Der Basis-Benutzername kann nur einmalig festgelegt werden. Sobald Sie speichern, ist diese Einstellung dauerhaft
							gesperrt und kann nicht mehr geändert werden. Bitte wählen Sie sorgfältig.
						</Typography>
						<Box
							sx={{
								width: '100%',
								backgroundColor: '#f7f8fb',
								borderRadius: '0.25rem',
								padding: '0.75rem 1rem',
							}}
						>
							<Typography textAlign='center' sx={{ fontWeight: 600, color: 'primary.main' }}>
								Basis-Benutzername: {basisBenutzername.trim()}
							</Typography>
						</Box>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: '1.5rem', pb: '1.5rem', pt: 0, justifyContent: 'center' }}>
					<Button variant='outlined' size='small' sx={{ minWidth: '7.5rem' }} onClick={handleCloseCreateBasisBenutzernameDialog}>
						Abbrechen
					</Button>
					<Button
						variant='contained'
						size='small'
						sx={{ minWidth: '7.5rem' }}
						onClick={handleConfirmCreateBasisBenutzername}
						disabled={isCreateBasisBenutzernameDisabled}
					>
						Bestätigen & Sperren
					</Button>
				</DialogActions>
			</Dialog>
			<Stack direction={'row'} justifyContent={'space-between'} alignItems={'flex-end'} sx={{ mb: '1rem', mt: '0.625rem' }}>
				<Typography variant='h3' component={'h2'}>
					Personenverwaltung
				</Typography>

				{isauthenticatedAdminUser && (
					<Button endIcon={<AddIcon />} onClick={() => setActiveShowCase('addPerson')} disabled={!isBasisBenutzernameLocked}>
						Hinzufügen
					</Button>
				)}
			</Stack>
			<InhaltWrapper marginBottom={'2rem'}>
				<Typography>
					Hier können Sie weitere Personen in Ihren Account einladen und deren Rechte verwalten.{' '}
					<strong>
						Bitte beachten Sie, dass alle Personen mit dem Status “aktiv” Zugriff auf sämtliche Projekte in Ihrem Account haben, d.h. diese
						einsehen und bearbeiten können
					</strong>
					. Eine zeitliche Begrenzung der Zugriffsrechte ist möglich, indem Sie den Nutzer “inaktiv” schalten.
				</Typography>
				<Typography>
					Geben Sie im ersten Schritt einen <strong>“Basis-Benutzernamen”</strong> an. Dieser wird dann automatisch als Präfix für die
					Benutzernamen der eingeladenen Personen genutzt. Bitte beachten Sie, dass dieser Name einmalig festgelegt wird und nicht mehr
					veränderbar ist.
				</Typography>

				<Box
					sx={{
						mt: '1.5rem',
						p: '1rem',
						backgroundColor: '#effafe',
						borderRadius: '0.25rem',
						border: '1px solid #d5edf6',
					}}
				>
					<Typography sx={{ fontWeight: 600, mb: '0.75rem' }}>Basis-Benutzername</Typography>
					<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
						<Box sx={{ width: '100%' }}>
							<CreateForm
								formStruc={getFormStrucBasisBenutzername(isBasisBenutzernameLocked)}
								formValues={benutzernameFormValues}
								updateFormValuesFunc={handleBasisBenutzernameChange}
								size='small'
							/>
						</Box>
						<Button endIcon={<AddIcon />} onClick={handleOpenCreateBasisBenutzernameDialog} disabled={isCreateBasisBenutzernameDisabled}>
							Basis-Benutzername erstellen
						</Button>
					</Stack>
					<Typography variant='caption' sx={{ display: 'block', mt: '0.5rem' }}>
						Benutzernamen werden automatisch generiert: {basisBenutzernamePreviewPrefix}_001, {basisBenutzernamePreviewPrefix}_002, ...
					</Typography>
				</Box>
				<InhaltTrenner marginTop='4rem' />
				<Typography>
					<strong>Sie betreuen mehrere Projekte und benötigen einen getrennten Zugriff für verschiedene Personen?</strong> <br></br>Dann
					empfehlen wir Ihnen, für jedes Projekt einen eigenen Account anzulegen (mit unterschiedlichen Benutzernamen; die E-Mail-Adresse kann
					identisch bleiben).
				</Typography>
				<InhaltTrenner />
				<Typography variant='h6' fontWeight={600}>
					Übersicht Personenverwaltung
				</Typography>
				<Typography>
					Der Status eingeladener Personen lässt sich nur durch einen Benutzer mit administrativen Rechten ändern. Mit einem Klick auf den Namen
					kommen Sie auf das Profil und können dort die Anpassungen vornehmen.
				</Typography>
			</InhaltWrapper>
			<DataGridPremium
				loading={wsActive}
				columns={columns}
				rows={rows}
				autoHeight
				pagination
				pageSizeOptions={[5, 10, 25, 50, 100]}
				onRowClick={
					isauthenticatedAdminUser ?
						(param) => {
							setActiveShowCase('editPerson');
							setPersonIdToEdit(get(param, 'row.id'));
						}
					:	undefined
				}
				initialState={{
					pagination: {
						paginationModel: { pageSize: 10, page: 0 },
					},
				}}
				localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
			/>
		</>
	);
};
export default NutzerVerwaltung;

export const hasAdminRole = (role: string): boolean => {
	return role === BENUTZERDATEN_PORTAL_ROLLE.Admin ? true : false;
};

const getFormStrucBasisBenutzername = (disabled: boolean): FormStrucItem[] => {
	return [
		{
			fields: [
				{
					...P.Organisation.schema.attributes.Mailbenachrichtung_Praefix_Fuer_Benutzernamen,
					placeholder: 'Eingabe des Basis-Benutzernamens',
					hideLabel: true,
					disabled,
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
};
